import React from "react";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { SunIcon, MoonIcon } from "../DaytimeIcon";
import { Day, TimeRange, WeeklyCalendarProps } from "./types";
import {
  DAYS,
  END_HOUR_OPTIONS,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  TOTAL_SLOTS,
} from "./constants";
import {
  canCopyAllEventsToDay,
  findAvailableEventSlot,
  getDayEnabledRange,
  isDayDisabled,
  slotIndexToTime,
  timeToSlotIndex,
} from "./utils";
import { useScheduleActions } from "./useScheduleActions";
import {
  AddEventButton,
  MobileCopyButton,
  MobileDayBox,
  MobileDayLabel,
  MobileDayRow,
  MobileDeleteButton,
  MobileEventRow,
  MobileList,
  TimeSelect,
  TimeSelectGroup,
  TimeSeparator,
} from "./WeeklyCalendarMobile.styles";

const WeeklyCalendarMobile: React.FC<WeeklyCalendarProps> = ({
  weekdaysEnabled = false,
  weekendEnabled = false,
  showSun = false,
  showMoon = false,
  value,
  onChange,
}) => {
  const { copyFromPreviousDay, deleteEvent } = useScheduleActions(
    value,
    onChange,
    weekdaysEnabled,
    weekendEnabled
  );

  const handleAddEvent = (day: Day) => {
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    const dayRanges = value[day.key] || [];
    const newEvent = findAvailableEventSlot(dayRanges, range);
    if (!newEvent) return;
    onChange({ ...value, [day.key]: [...dayRanges, newEvent] });
  };

  const handleTimeChange = (
    day: Day,
    eventIndex: number,
    field: "startH" | "startM" | "endH" | "endM",
    val: string
  ) => {
    const dayRanges = [...(value[day.key] || [])];
    const event = [...dayRanges[eventIndex]] as TimeRange;
    const [startH, startM] = event[0].split(":");
    const [endH, endM] = event[1].split(":");

    let nSH = startH, nSM = startM, nEH = endH, nEM = endM;
    switch (field) {
      case "startH": nSH = val; break;
      case "startM": nSM = val; break;
      case "endH": nEH = val; if (val === "24") nEM = "00"; break;
      case "endM": nEM = val; break;
    }

    const newStartIdx = timeToSlotIndex(`${nSH}:${nSM}`);
    let newEndIdx = timeToSlotIndex(`${nEH}:${nEM}`);

    const isStartChange = field === "startH" || field === "startM";

    // Si al mover la hora de inicio la franja queda invertida (fin <= inicio),
    // desplazamos también la hora fin para mantener la duración original.
    // Al modificar la hora fin dejándola anterior al inicio no se permite el cambio.
    if (newStartIdx >= newEndIdx) {
      if (!isStartChange) return;
      const duration = timeToSlotIndex(event[1]) - timeToSlotIndex(event[0]);
      newEndIdx = Math.min(newStartIdx + duration, TOTAL_SLOTS);
    }

    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (range && (newStartIdx < range.minSlot || newEndIdx > range.maxSlot)) return;

    const overlaps = dayRanges.some((r, idx) => {
      if (idx === eventIndex) return false;
      const s = timeToSlotIndex(r[0]);
      const e = timeToSlotIndex(r[1]);
      return newStartIdx < e && s < newEndIdx;
    });
    if (overlaps) return;

    dayRanges[eventIndex] = [slotIndexToTime(newStartIdx), slotIndexToTime(newEndIdx)];
    onChange({ ...value, [day.key]: dayRanges });
  };

  return (
    <MobileList>
      {DAYS.map((day, dayIndex) => {
        const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
        const disabled = !range;
        const events = (!disabled && value[day.key]) || [];
        const isFirstDay = dayIndex === 0;
        const prevDay = !isFirstDay ? DAYS[dayIndex - 1] : null;
        const prevDayDisabled = prevDay ? isDayDisabled(prevDay, weekdaysEnabled, weekendEnabled) : true;
        const prevDayEvents = prevDay ? (value[prevDay.key] || []) : [];
        const currentRange = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
        const canCopy = !isFirstDay && !disabled && prevDayEvents.length > 0 && !prevDayDisabled && currentRange !== null && canCopyAllEventsToDay(prevDayEvents, currentRange, events);
        const startHours = range
          ? HOUR_OPTIONS.filter(h => { const n = parseInt(h); return n >= Math.floor(range.minSlot / 2) && n <= Math.floor((range.maxSlot - 1) / 2); })
          : HOUR_OPTIONS;
        const endHours = range
          ? END_HOUR_OPTIONS.filter(h => { const n = parseInt(h); return n >= Math.floor(range.minSlot / 2) && n <= Math.floor(range.maxSlot / 2); })
          : END_HOUR_OPTIONS;
        return (
          <MobileDayBox key={day.key}>
            <MobileDayRow $disabled={disabled}>
              <MobileDayLabel>{day.fullLabel}</MobileDayLabel>
              <MobileCopyButton
                $disabled={!canCopy}
                onClick={() => canCopy && copyFromPreviousDay(dayIndex)}
              >
                <FileCopyIcon style={{ fontSize: 12 }} />
                Copiar del día anterior
              </MobileCopyButton>
            </MobileDayRow>
            {events.map((event, idx) => {
              const [sH, sM] = event[0].split(":");
              const [eH, eM] = event[1].split(":");
              const startSlot = timeToSlotIndex(event[0]);
              const endSlot = timeToSlotIndex(event[1]);
              const startIsDay = startSlot >= 18 && startSlot < 42;
              const endIsDay = endSlot > 18 && endSlot <= 42;
              const hasDaytime = startIsDay || endIsDay;
              const hasNighttime = !startIsDay || !endIsDay;
              return (
                <MobileEventRow key={idx}>
                  {startIsDay ? (
                    <>{hasDaytime && showSun && <SunIcon fontSize={14} />}{hasNighttime && showMoon && <MoonIcon fontSize={14} />}</>
                  ) : (
                    <>{hasNighttime && showMoon && <MoonIcon fontSize={14} />}{hasDaytime && showSun && <SunIcon fontSize={14} />}</>
                  )}
                  <TimeSelectGroup>
                    <TimeSelect value={sH} onChange={(e) => handleTimeChange(day, idx, "startH", e.target.value)}>
                      {startHours.map((h) => <option key={h} value={h}>{h}</option>)}
                    </TimeSelect>
                    :
                    <TimeSelect value={sM} onChange={(e) => handleTimeChange(day, idx, "startM", e.target.value)}>
                      {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </TimeSelect>
                  </TimeSelectGroup>
                  <TimeSeparator>-</TimeSeparator>
                  <TimeSelectGroup>
                    <TimeSelect value={eH} onChange={(e) => handleTimeChange(day, idx, "endH", e.target.value)}>
                      {endHours.map((h) => <option key={h} value={h}>{h}</option>)}
                    </TimeSelect>
                    :
                    <TimeSelect
                      value={eM}
                      disabled={eH === "24"}
                      onChange={(e) => handleTimeChange(day, idx, "endM", e.target.value)}
                    >
                      {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </TimeSelect>
                  </TimeSelectGroup>
                  <MobileDeleteButton onClick={() => deleteEvent(day, idx)}>✕</MobileDeleteButton>
                </MobileEventRow>
              );
            })}
            {!disabled && (
              <AddEventButton onClick={() => handleAddEvent(day)}>+ Añadir franja horaria</AddEventButton>
            )}
          </MobileDayBox>
        );
      })}
    </MobileList>
  );
};

export default WeeklyCalendarMobile;
